import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useFieldContext } from '@bridebook/aura';
import { cn } from '../../lib/utils/cn';
import { Icon, type SvgIcon } from '../icon/icon';
import { ClearButton } from './clear-button';
import {
  inputBaseLayout,
  inputBaseTypography,
  inputBaseTransition,
  inputBaseRing,
  inputErrorVariant,
  inputDisabledVariant,
  inputWithLeftElementVariant,
} from './input-variants';

const fakeInputVariants = cva(
  cn(
    inputBaseLayout,
    inputBaseTypography,
    'text-left',
    inputBaseTransition,
    'disabled:cursor-not-allowed',
    inputBaseRing,
    'focus-visible:bg-white focus-visible:ring-2 data-[focused=true]:bg-white data-[focused=true]:ring-2',
  ),
  {
    variants: {
      variant: {
        default:
          'bg-space-04 group-hover:data-[error=false]:data-[focused=false]:not-disabled:bg-space-08 data-[focused=true]:bg-white',
        white:
          'border border-space-40 bg-white group-hover:data-[error=false]:data-[focused=false]:not-disabled:bg-space-04 data-[focused=true]:border-transparent',
      },
      error: inputErrorVariant,
      disabled: inputDisabledVariant,
      withLeftElement: inputWithLeftElementVariant,
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      disabled: false,
    },
  },
);

const hasInputValue = (value?: string) => {
  if (value === undefined || value === null) {
    return false;
  }
  return String(value).length > 0;
};

export interface FakeInputProps
  extends Omit<React.ComponentPropsWithoutRef<'button'>, 'children' | 'disabled' | 'onClick'>,
    Omit<VariantProps<typeof fakeInputVariants>, 'withLeftElement'> {
  focused?: boolean;
  classNames?: {
    wrapper?: string;
    icon?: string;
    iconRight?: string;
    clearButton?: string;
  };
  icon?: SvgIcon;
  iconRight?: SvgIcon;
  placeholder?: string;
  value?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  showClearBtn?: boolean;
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/**
 * Visual input shell used by date/time wrappers.
 * It renders input-like UI, but no native picker logic.
 */
export const FakeInput = forwardRef<HTMLButtonElement, FakeInputProps>(
  (
    {
      className,
      classNames,
      icon,
      iconRight,
      placeholder,
      value,
      onClick,
      variant,
      error: errorProp,
      disabled: disabledProp,
      focused = false,
      showClearBtn = false,
      onClear,
      ...buttonProps
    },
    ref,
  ) => {
    const context = useFieldContext();
    const disabled = disabledProp ?? context?.disabled ?? false;
    const error = errorProp ?? context?.error ?? false;

    const hasValue = hasInputValue(value);
    const shouldShowClearBtn = !!showClearBtn && hasValue;

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      // Keep clear action from also triggering the main input button click.
      event.stopPropagation();
      onClear?.(event);
    };

    return (
      <div className={cn('group relative', classNames?.wrapper)}>
        <button
          ref={ref}
          type="button"
          data-error={error ? 'true' : 'false'}
          data-focused={focused ? 'true' : 'false'}
          className={cn(
            fakeInputVariants({
              error,
              disabled,
              withLeftElement: !!icon,
              variant,
            }),
            shouldShowClearBtn && iconRight && 'pr-14',
            shouldShowClearBtn && !iconRight && 'pr-10',
            !shouldShowClearBtn && iconRight && 'pr-10',
            className,
          )}
          disabled={disabled}
          onClick={onClick}
          {...buttonProps}>
          {icon && (
            <Icon
              icon={icon}
              className={cn(
                'pointer-events-none absolute top-1/2 left-4 z-10 size-[16px] -translate-y-1/2 text-space',
                classNames?.icon,
              )}
            />
          )}
          <span className={cn(hasValue ? 'text-space' : 'text-space-60')}>
            {hasValue ? value : placeholder}
          </span>
        </button>

        {shouldShowClearBtn && (
          // Clear button stays as a sibling (not nested inside the trigger button).
          // When iconRight is present, the clear button shifts left so the icon stays pinned
          // to the far right (mirrors Select's chevron + clear pattern).
          <ClearButton
            disabled={disabled}
            onClick={handleClear}
            className={cn(iconRight && 'right-10', classNames?.clearButton)}
          />
        )}

        {iconRight && (
          <Icon
            icon={iconRight}
            className={cn(
              'pointer-events-none absolute top-1/2 right-4 z-10 size-[16px] -translate-y-1/2 text-space',
              classNames?.iconRight,
            )}
          />
        )}
      </div>
    );
  },
);

FakeInput.displayName = 'FakeInput';
